import { useState, useRef, useEffect } from 'react'
import {
  FiX,
  FiUpload,
  FiCheck,
  FiAlertCircle,
  FiCamera,
  FiVideo,
  FiFileText,
  FiHelpCircle,
  FiInfo,
} from 'react-icons/fi'
import { useAccount } from 'wagmi'
import { submitCardWithMintCredit, getSignedUploadUrl, uploadFileToGCS } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import Toast from './Toast'
import { useToast } from '../hooks/useToast'
import OwnershipDeclarationModal from './OwnershipDeclarationModal'
import { useAuthSession } from '../contexts/AuthSessionContext'
import {
  SHIPPING_COUNTRIES,
  requiresStateProvince,
  resolveShippingCountry,
} from '../utils/shippingCountries'
import {
  inspectWebVideoFiles,
  webVideoAlertTitle,
  webVideoAlertBody,
  WEB_VIDEO_ACCEPT,
  WEB_VIDEO_DROPZONE_HINT,
} from '../utils/webVideoCompatibility'

function HelpBalloon({ text }) {
  const [show, setShow] = useState(false)
  return (
    <span className="relative inline-flex ml-1.5 align-middle">
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        className="w-5 h-5 rounded-full flex items-center justify-center text-gray-500 hover:text-cyan-400 hover:bg-white/10 transition-colors flex-shrink-0"
        aria-label="Help"
      >
        <FiHelpCircle size={14} />
      </button>
      {show && (
        <span
          className="absolute left-0 top-full mt-1.5 z-20 px-3 py-2 rounded-xl bg-gray-800/95 border border-white/15 text-xs text-gray-200 shadow-xl max-w-[260px] whitespace-normal leading-relaxed"
          style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
        >
          {text}
          <span className="absolute -top-1.5 left-3 w-2.5 h-2.5 bg-gray-800/95 border-l border-t border-white/15 transform rotate-45" />
        </span>
      )}
    </span>
  )
}

/** ownerUserId: M1NT user UUID (required). Declaration: wallet signature if connected, else email session attestation. */
export default function SubmitCardModal({
  isOpen,
  onClose,
  wallet: _legacyWallet,
  ownerUserId,
  onSuccess,
}) {
  const { toasts, showToast, removeToast } = useToast()
  const { address: connectedAddress } = useAccount()
  const { user: sessionUser, emailAuthToken } = useAuthSession()
  /** Email attestation when logged in with email (even if a browser wallet is connected). Wallet only if no email session. */
  const declarationVariant =
    emailAuthToken && sessionUser?.email ? 'email' : connectedAddress ? 'wallet' : null
  const [formData, setFormData] = useState({
    name: '',
    cardType: 'Pokemon',
    set: '',
    year: '',
    rarity: '',
    number: '',
  })

  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    countryOther: '',
  })

  const [cardStory, setCardStory] = useState('')
  const [videoFiles, setVideoFiles] = useState([])
  const [documentFiles, setDocumentFiles] = useState([])

  const [frontImage, setFrontImage] = useState(null)
  const [backImage, setBackImage] = useState(null)
  const [frontPreview, setFrontPreview] = useState(null)
  const [backPreview, setBackPreview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [invalidFields, setInvalidFields] = useState({})
  const [step, setStep] = useState('form') // 'form', 'uploading', 'success'
  const [cameraOpen, setCameraOpen] = useState(false)
  const [cameraSide, setCameraSide] = useState(null) // 'front' or 'back'
  const [stream, setStream] = useState(null)
  const [showDeclarationModal, setShowDeclarationModal] = useState(false)
  const [declarationPayload, setDeclarationPayload] = useState(null)

  const frontFileInputRef = useRef(null)
  const frontCameraInputRef = useRef(null)
  const backFileInputRef = useRef(null)
  const backCameraInputRef = useRef(null)
  const videoRef = useRef(null)
  const videoInputRef = useRef(null)
  const documentInputRef = useRef(null)
  const formScrollRef = useRef(null)

  // Cleanup stream on unmount or when camera closes
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop())
      }
    }
  }, [stream])

  // Set video source when stream is available
  useEffect(() => {
    if (stream && videoRef.current) {
      videoRef.current.srcObject = stream
    }
  }, [stream])

  if (!isOpen) return null

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (error) setError(null)
    setInvalidFields((prev) => ({ ...prev, [name]: false }))
  }

  const handleImageChange = (e, side) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file')
      return
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('Image must be smaller than 10MB')
      return
    }

    if (side === 'front') {
      setFrontImage(file)
      setFrontPreview(URL.createObjectURL(file))
    } else {
      setBackImage(file)
      setBackPreview(URL.createObjectURL(file))
    }

    // Reset input so the same file can be selected again
    e.target.value = ''
  }

  const triggerFileInput = (side) => {
    if (side === 'front') {
      frontFileInputRef.current?.click()
    } else {
      backFileInputRef.current?.click()
    }
  }

  const triggerCameraInput = async (side) => {
    // Check if getUserMedia is supported
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      // Fallback to file input for older browsers or if API not available
      if (side === 'front') {
        frontCameraInputRef.current?.click()
      } else {
        backCameraInputRef.current?.click()
      }
      return
    }

    try {
      // Request camera permission
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Use back camera on mobile if available
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
      })

      setStream(mediaStream)
      setCameraSide(side)
      setCameraOpen(true)
    } catch (err) {
      console.error('Error accessing camera:', err)
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setError('Camera permission denied. Please allow camera access in your browser settings.')
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setError('No camera found. Please use the file upload option.')
      } else {
        setError('Error accessing camera. Please try again or use the upload option.')
      }
    }
  }

  const closeCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      setStream(null)
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setCameraOpen(false)
    setCameraSide(null)
  }

  const capturePhoto = () => {
    if (!videoRef.current || !cameraSide) return

    const video = videoRef.current
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    ctx.drawImage(video, 0, 0)

    canvas.toBlob(
      (blob) => {
        if (!blob) return

        // Create a File object from the blob
        const file = new File([blob], `card-${cameraSide}-${Date.now()}.jpg`, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          setError('The captured image is too large. Please try again.')
          closeCamera()
          return
        }

        // Set the image
        if (cameraSide === 'front') {
          setFrontImage(file)
          setFrontPreview(URL.createObjectURL(file))
        } else {
          setBackImage(file)
          setBackPreview(URL.createObjectURL(file))
        }

        closeCamera()
      },
      'image/jpeg',
      0.9
    )
  }

  const removeImage = (side) => {
    if (side === 'front') {
      if (frontPreview) URL.revokeObjectURL(frontPreview)
      setFrontImage(null)
      setFrontPreview(null)
    } else {
      if (backPreview) URL.revokeObjectURL(backPreview)
      setBackImage(null)
      setBackPreview(null)
    }
  }

  const handleVideosChange = async (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (!files.length) return
    const inspected = await inspectWebVideoFiles(files)
    const rejects = inspected.filter((r) => !r.ok)
    if (rejects.length > 0) {
      setError(`${webVideoAlertTitle(rejects)}\n${webVideoAlertBody(rejects)}`)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return
    }
    setError(null)
    const okFiles = inspected.map((r) => r.file)
    setVideoFiles((prev) => [...prev, ...okFiles].slice(0, 5))
  }

  const handleDocumentsChange = (e) => {
    const files = Array.from(e.target.files || [])
    const valid = files.filter((f) => f.type === 'application/pdf')
    if (valid.length < files.length) setError('Only PDF documents are allowed')
    setDocumentFiles((prev) => [...prev, ...valid].slice(0, 5))
    e.target.value = ''
  }

  const removeVideo = (index) => setVideoFiles((prev) => prev.filter((_, i) => i !== index))
  const removeDocument = (index) => setDocumentFiles((prev) => prev.filter((_, i) => i !== index))

  const invalidInputClass = 'border-red-500/70 bg-red-500/10 focus:border-red-500/80'

  const validateForm = () => {
    const invalid = {}
    if (!formData.name.trim()) {
      invalid.name = true
      setError('Card name is required')
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (!formData.cardType) {
      invalid.cardType = true
      setError('Card type is required')
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (!shippingAddress.street.trim()) {
      invalid.street = true
      setError('Street address is required')
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (!shippingAddress.city.trim()) {
      invalid.city = true
      setError('City is required')
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    const { code: countryCode, label: countryLabel } = resolveShippingCountry(
      shippingAddress.country,
      shippingAddress.countryOther
    )
    if (!countryLabel) {
      setError(
        shippingAddress.country === 'OTHER'
          ? 'Please enter your country name'
          : 'Please select your country'
      )
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (requiresStateProvince(countryCode) && !shippingAddress.state.trim()) {
      invalid.state = true
      setError('State or province is required for your country')
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (!shippingAddress.zipCode.trim()) {
      invalid.zipCode = true
      setError('Postal code is required')
      setInvalidFields(invalid)
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (!ownerUserId) {
      setError('Account not loaded. Please refresh and try again.')
      setInvalidFields({})
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    if (!declarationVariant) {
      setError('Sign in with email or connect a wallet to complete the ownership declaration.')
      setInvalidFields({})
      formScrollRef.current?.scrollTo?.({ top: 0, behavior: 'smooth' })
      return false
    }
    setInvalidFields({})
    return true
  }

  /** Browsers often leave `file.type` empty; policy must match the real object Content-Type or GCS returns 403. */
  const guessVideoContentTypeForPolicy = (file) => {
    if (file?.type) return file.type
    const ext = (file?.name?.split('.').pop() || '').toLowerCase()
    const byExt = {
      mp4: 'video/mp4',
      m4v: 'video/mp4',
      mov: 'video/quicktime',
      qt: 'video/quicktime',
      webm: 'video/webm',
    }
    return byExt[ext] || 'video/mp4'
  }

  const doSubmit = async (payload) => {
    const DIRECT_UPLOAD_THRESHOLD_BYTES = 30 * 1024 * 1024 // 30MB
    setLoading(true)
    setError(null)
    setInvalidFields({})
    setStep('uploading')
    try {
      if (videoFiles.length > 0) {
        const inspected = await inspectWebVideoFiles(videoFiles)
        const rejects = inspected.filter((r) => !r.ok)
        if (rejects.length > 0) {
          setError(`${webVideoAlertTitle(rejects)}\n${webVideoAlertBody(rejects)}`)
          setStep('form')
          return
        }
      }
      const submitData = new FormData()
      submitData.append('name', formData.name.trim())
      submitData.append('cardType', formData.cardType)
      submitData.append('ownerId', ownerUserId)
      submitData.append(
        'attributes',
        JSON.stringify({
          ...(formData.set && { set: formData.set.trim() }),
          ...(formData.year && { year: parseInt(formData.year) }),
          ...(formData.rarity && { rarity: formData.rarity.trim() }),
          ...(formData.number && { number: formData.number.trim() }),
        })
      )
      const { code: shipCountryCode, label: shipCountryLabel } = resolveShippingCountry(
        shippingAddress.country,
        shippingAddress.countryOther
      )
      submitData.append(
        'shippingAddress',
        JSON.stringify({
          street: shippingAddress.street.trim(),
          city: shippingAddress.city.trim(),
          zipCode: shippingAddress.zipCode.trim(),
          country: shipCountryLabel,
          countryCode: shipCountryCode,
          ...(shippingAddress.state.trim() ? { state: shippingAddress.state.trim() } : {}),
        })
      )
      if (cardStory.trim()) submitData.append('cardStory', cardStory.trim())
      const largeVideos = videoFiles.filter(
        (f) => Number(f?.size || 0) > DIRECT_UPLOAD_THRESHOLD_BYTES
      )
      const smallVideos = videoFiles.filter(
        (f) => Number(f?.size || 0) <= DIRECT_UPLOAD_THRESHOLD_BYTES
      )
      const directVideoGsUrls = []

      for (const file of largeVideos) {
        const signedRes = await getSignedUploadUrl(
          file.name,
          guessVideoContentTypeForPolicy(file),
          'owner-uploads',
          _legacyWallet || null
        )
        const signedPayload = signedRes?.data?.data ?? signedRes?.data
        const gsUrl = await uploadFileToGCS(signedPayload, file)
        directVideoGsUrls.push(gsUrl)
      }

      smallVideos.forEach((f) => submitData.append('videos', f))
      if (directVideoGsUrls.length > 0) {
        submitData.append('quoteVideoUrls', JSON.stringify(directVideoGsUrls))
      }
      documentFiles.forEach((f) => submitData.append('documents', f))
      if (frontImage) submitData.append('frontImage', frontImage)
      if (backImage) submitData.append('backImage', backImage)
      if (payload?.method === 'email') {
        submitData.append('ownerDeclarationMethod', 'email')
        submitData.append('ownerDeclarationSignedAt', payload.signedAt)
        submitData.append('ownerDeclarationMessage', payload.message)
      } else if (payload?.signature && payload?.wallet) {
        submitData.append('ownerDeclarationSignedAt', payload.signedAt)
        submitData.append('ownerDeclarationMessage', payload.message)
        submitData.append('ownerDeclarationSignature', payload.signature)
        submitData.append('ownerDeclarationWallet', payload.wallet)
      }
      console.log('📤 Submitting card with an M1NT credit...')
      const response = await submitCardWithMintCredit(submitData)
      console.log('✅ Card submitted:', response.data)
      setStep('success')
      const cardName = formData.name?.trim() || 'Card'
      showToast(
        `Submitted with 1 M1NT credit. "${cardName}" is prepaid—M1NT Lab will assign a grader and send shipping instructions.`,
        'success'
      )
      window.dispatchEvent(
        new CustomEvent('newNotification', {
          detail: {
            notification: {
              message: 'Your card was submitted using an M1NT credit',
              type: 'success',
            },
          },
        })
      )
      setTimeout(() => {
        onSuccess && onSuccess()
        handleClose()
      }, 2000)
    } catch (err) {
      console.error('Error submitting card:', err)
      setError(err.response?.data?.error || err.message || 'Failed to submit card')
      setStep('form')
      showToast(
        err.response?.data?.error || 'Failed to submit card with an M1NT credit. Please try again.',
        'error'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitCard = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    if (!declarationPayload) {
      setShowDeclarationModal(true)
      return
    }
    await doSubmit(declarationPayload)
  }

  const handleDeclarationAccept = (payload) => {
    setDeclarationPayload(payload)
    setShowDeclarationModal(false)
    doSubmit(payload)
  }

  const handleClose = () => {
    // Close camera if open
    closeCamera()

    // Cleanup preview URLs
    if (frontPreview) URL.revokeObjectURL(frontPreview)
    if (backPreview) URL.revokeObjectURL(backPreview)

    // Reset state
    setFormData({
      name: '',
      cardType: 'Pokemon',
      set: '',
      year: '',
      rarity: '',
      number: '',
    })
    setShippingAddress({
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      countryOther: '',
    })
    setFrontImage(null)
    setBackImage(null)
    setFrontPreview(null)
    setBackPreview(null)
    setCardStory('')
    setVideoFiles([])
    setDocumentFiles([])
    setError(null)
    setStep('form')
    setLoading(false)
    setDeclarationPayload(null)
    setShowDeclarationModal(false)
    setInvalidFields({})

    onClose()
  }

  const cardTypes = [
    { value: 'Pokemon', label: 'Pokémon' },
    { value: 'Magic', label: 'Magic: The Gathering' },
    { value: 'YuGiOh', label: 'Yu-Gi-Oh!' },
    { value: 'Sports', label: 'Sports Cards' },
    { value: 'DragonBall', label: 'Dragon Ball' },
    { value: 'OnePiece', label: 'One Piece' },
    { value: 'Other', label: 'Other' },
  ]

  return (
    <>
      <OwnershipDeclarationModal
        isOpen={showDeclarationModal}
        onClose={() => setShowDeclarationModal(false)}
        onAccept={handleDeclarationAccept}
        variant={declarationVariant === 'email' ? 'email' : 'wallet'}
        signingWallet={connectedAddress}
        attestationEmail={sessionUser?.email || null}
      />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4 modal-overlay">
        <div
          className="modal-content max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-white/5">
            <div>
              <h2 className="text-2xl font-bold text-white">Submit a card</h2>
              <p className="text-sm text-gray-400 mt-1">
                Submit your card for grading using 1 M1NT credit
              </p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/5 rounded-xl transition-colors text-gray-400 hover:text-white"
              disabled={loading}
            >
              <FiX size={24} />
            </button>
          </div>

          {/* Content */}
          <div ref={formScrollRef} className="overflow-y-auto max-h-[calc(90vh-180px)]">
            {step === 'success' ? (
              <div className="p-12 text-center">
                <div
                  className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
                  style={{
                    background: 'rgba(143, 216, 230, 0.2)',
                    border: '1px solid rgba(143, 216, 230, 0.3)',
                  }}
                >
                  <FiCheck className="text-emerald-400" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-2 text-white">Submission Sent!</h3>
                <p className="text-gray-400">
                  M1NT Lab will review your submission and continue your grading flow.
                  <br />
                  Check your dashboard and email for updates.
                </p>
              </div>
            ) : step === 'uploading' ? (
              <div className="p-12 text-center">
                <LoadingSpinner size="lg" text="Submitting your card with an M1NT credit..." />
              </div>
            ) : (
              <form onSubmit={handleSubmitCard} className="p-6 space-y-8" noValidate>
                {/* Error Message */}
                {error && (
                  <div
                    className="p-4 rounded-xl flex items-start gap-3"
                    style={{
                      background: 'rgba(227, 184, 114, 0.1)',
                      border: '1px solid rgba(227, 184, 114, 0.3)',
                    }}
                  >
                    <FiAlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                    <span className="text-red-400 whitespace-pre-line">{error}</span>
                  </div>
                )}

                {/* Card Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b border-white/5 pb-2 text-white">
                    Card Information
                  </h3>

                  <div>
                    <label className="label inline-flex flex-wrap items-center gap-0">
                      Card Name <span className="text-red-400">*</span>
                      <HelpBalloon text="Usually found on the front of the card, below the image or in the corner." />
                    </label>
                    <p className="flex gap-2 text-xs text-gray-400 leading-relaxed mb-2 mt-0.5">
                      <FiInfo
                        className="shrink-0 w-3.5 h-3.5 text-cyan-400/90 mt-0.5"
                        aria-hidden
                      />
                      <span>
                        The official name used on your certificate and NFT is finalized by the M1NT
                        grader after physical inspection.
                      </span>
                    </p>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`input ${invalidFields.name ? invalidInputClass : ''}`}
                      placeholder="e.g., Charizard Holo 1st Edition"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="label inline-flex flex-wrap items-center gap-0">
                        Card Type <span className="text-red-400">*</span>
                        <HelpBalloon text="The category of your card (e.g. Pokémon, Sports, Trading Card Game)." />
                      </label>
                      <select
                        name="cardType"
                        value={formData.cardType}
                        onChange={handleInputChange}
                        className={`select ${invalidFields.cardType ? invalidInputClass : ''}`}
                        required
                      >
                        {cardTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="label inline-flex flex-wrap items-center gap-0">
                        Set / Edition
                        <HelpBalloon text="Printed on the card or packaging. e.g. Base Set, 1st Edition." />
                      </label>
                      <input
                        type="text"
                        name="set"
                        value={formData.set}
                        onChange={handleInputChange}
                        className="input"
                        placeholder="e.g., Base Set"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 items-end">
                    <div className="flex flex-col gap-1">
                      <label className="label min-h-[1.25rem] inline-flex flex-wrap items-center gap-0">
                        Year
                        <HelpBalloon text="The year the card was printed, often on the card back or packaging." />
                      </label>
                      <input
                        type="number"
                        name="year"
                        value={formData.year}
                        onChange={handleInputChange}
                        className="input"
                        placeholder="1999"
                        min="1900"
                        max={new Date().getFullYear() + 1}
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="label min-h-[1.25rem] inline-flex flex-wrap items-center gap-0">
                        Rarity
                        <HelpBalloon text="Usually on the card (e.g. Common, Rare, Holo Rare)." />
                      </label>
                      <input
                        type="text"
                        name="rarity"
                        value={formData.rarity}
                        onChange={handleInputChange}
                        className="input"
                        placeholder="Holo Rare"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="label min-h-[1.25rem] inline-flex flex-wrap items-center gap-0">
                        <span className="hidden sm:inline">Card Number</span>
                        <span className="sm:hidden">Card Num.</span>
                        <HelpBalloon text="The set number, e.g. 4/102 (card 4 of 102 in the set)." />
                      </label>
                      <input
                        type="text"
                        name="number"
                        value={formData.number}
                        onChange={handleInputChange}
                        className="input"
                        placeholder="4/102"
                      />
                    </div>
                  </div>
                </div>

                {/* Card Story */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b border-white/5 pb-2 text-white">
                    Card Story
                  </h3>
                  <p className="text-sm text-gray-400">
                    Tell us the story behind this card (optional). Provenance, how you got it, or
                    why it matters to you.
                  </p>
                  <textarea
                    value={cardStory}
                    onChange={(e) => setCardStory(e.target.value)}
                    className="input min-h-[100px] resize-y"
                    placeholder="e.g., This card was pulled from a booster box in 1999 and has been in my collection ever since..."
                    rows={4}
                  />
                </div>

                {/* Videos */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b border-white/5 pb-2 text-white">
                    Videos (optional)
                  </h3>
                  <p className="text-sm text-gray-400">
                    Optional. Upload videos of the card (e.g. condition, details, famous people with
                    the card, or signing/autographing). Max 5 files.
                  </p>
                  <p className="text-xs text-gray-500">{WEB_VIDEO_DROPZONE_HINT}</p>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept={WEB_VIDEO_ACCEPT}
                    multiple
                    onChange={handleVideosChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => videoInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                    style={{
                      background: 'rgba(180, 156, 245, 0.1)',
                      border: '1px solid rgba(180, 156, 245, 0.3)',
                      color: '#B49CF5',
                    }}
                  >
                    <FiVideo size={18} />
                    Add videos
                  </button>
                  {videoFiles.length > 0 && (
                    <ul className="space-y-2">
                      {videoFiles.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 text-sm"
                        >
                          <span className="text-gray-300 truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => removeVideo(i)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <FiX size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* PDF Documents */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b border-white/5 pb-2 text-white">
                    PDF Documents
                  </h3>
                  <p className="text-sm text-gray-400">
                    Optional. Attach PDFs (e.g. certificates, authenticity docs). Max 5 files.
                  </p>
                  <input
                    ref={documentInputRef}
                    type="file"
                    accept=".pdf,application/pdf"
                    multiple
                    onChange={handleDocumentsChange}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => documentInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors"
                    style={{
                      background: 'rgba(180, 156, 245, 0.1)',
                      border: '1px solid rgba(180, 156, 245, 0.3)',
                      color: '#B49CF5',
                    }}
                  >
                    <FiFileText size={18} />
                    Add PDF documents
                  </button>
                  {documentFiles.length > 0 && (
                    <ul className="space-y-2">
                      {documentFiles.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 text-sm"
                        >
                          <span className="text-gray-300 truncate">{f.name}</span>
                          <button
                            type="button"
                            onClick={() => removeDocument(i)}
                            className="text-red-400 hover:text-red-300 p-1"
                          >
                            <FiX size={16} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Shipping Address Section */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b border-white/5 pb-2 text-white">
                    Shipping Address
                  </h3>
                  <p className="text-sm text-gray-400">
                    Where should the grader send the card after evaluation?
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label className="label">
                        Street Address <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={shippingAddress.street}
                        onChange={(e) => {
                          setShippingAddress({ ...shippingAddress, street: e.target.value })
                          setError(null)
                          setInvalidFields((prev) => ({ ...prev, street: false }))
                        }}
                        className={`input ${invalidFields.street ? invalidInputClass : ''}`}
                        placeholder="123 Main Street"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">
                          City <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.city}
                          onChange={(e) => {
                            setShippingAddress({ ...shippingAddress, city: e.target.value })
                            setError(null)
                            setInvalidFields((prev) => ({ ...prev, city: false }))
                          }}
                          className={`input ${invalidFields.city ? invalidInputClass : ''}`}
                          placeholder="Los Angeles"
                          required
                        />
                      </div>

                      <div>
                        <label className="label">
                          State / province
                          {requiresStateProvince(shippingAddress.country) ? (
                            <span className="text-red-400"> *</span>
                          ) : (
                            <span className="text-gray-500 font-normal"> (optional)</span>
                          )}
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.state}
                          onChange={(e) => {
                            setShippingAddress({ ...shippingAddress, state: e.target.value })
                            setError(null)
                            setInvalidFields((prev) => ({ ...prev, state: false }))
                          }}
                          className={`input ${invalidFields.state ? invalidInputClass : ''}`}
                          placeholder={
                            requiresStateProvince(shippingAddress.country) ? 'CA' : 'Region'
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="label">
                          Postal / ZIP code <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.zipCode}
                          onChange={(e) => {
                            setShippingAddress({ ...shippingAddress, zipCode: e.target.value })
                            setError(null)
                            setInvalidFields((prev) => ({ ...prev, zipCode: false }))
                          }}
                          className={`input ${invalidFields.zipCode ? invalidInputClass : ''}`}
                          placeholder="90001"
                          required
                        />
                      </div>

                      <div>
                        <label className="label">
                          Country <span className="text-red-400">*</span>
                        </label>
                        <select
                          value={shippingAddress.country}
                          onChange={(e) =>
                            setShippingAddress({
                              ...shippingAddress,
                              country: e.target.value,
                              countryOther:
                                e.target.value === 'OTHER' ? shippingAddress.countryOther : '',
                            })
                          }
                          className="input"
                          required
                          aria-label="Country"
                        >
                          <option value="">Select country</option>
                          {SHIPPING_COUNTRIES.map((c) => (
                            <option key={c.code} value={c.code}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {shippingAddress.country === 'OTHER' ? (
                      <div>
                        <label className="label">
                          Country name <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          value={shippingAddress.countryOther}
                          onChange={(e) =>
                            setShippingAddress({
                              ...shippingAddress,
                              countryOther: e.target.value,
                            })
                          }
                          className="input"
                          placeholder="Enter your country"
                          required
                        />
                      </div>
                    ) : null}
                  </div>
                </div>

                {/* Image Upload */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b border-white/5 pb-2 text-white">
                    Card Images
                  </h3>
                  <p className="text-sm text-gray-400">
                    Upload high-quality photos of both sides of your card. Optional but recommended
                    for smoother intake.
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {/* Front Image */}
                    <div>
                      <label className="label">Front</label>
                      <div
                        className={`
                        relative aspect-[3/4] border-2 border-dashed rounded-xl 
                        flex flex-col items-center justify-center cursor-pointer
                        transition-all duration-300
                        ${
                          frontPreview
                            ? 'border-transparent'
                            : 'border-white/10 hover:border-cyan-500/50 hover:bg-white/5'
                        }
                      `}
                      >
                        {frontPreview ? (
                          <>
                            <img
                              src={frontPreview}
                              alt="Front preview"
                              className="w-full h-full object-cover rounded-xl"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage('front')}
                              className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-lg transition-colors"
                            >
                              <FiX size={16} />
                            </button>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-3 sm:p-4 gap-2 sm:gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                triggerFileInput('front')
                              }}
                              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
                              style={{
                                background: 'rgba(180, 156, 245, 0.1)',
                                border: '1px solid rgba(180, 156, 245, 0.2)',
                              }}
                              aria-label="Upload front image"
                            >
                              <FiUpload className="text-cyan-400" size={24} />
                            </button>
                            <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                            <div className="flex flex-col sm:flex-row gap-2 w-full max-w-[200px] sm:max-w-none">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  triggerFileInput('front')
                                }}
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                                style={{
                                  background: 'rgba(180, 156, 245, 0.1)',
                                  border: '1px solid rgba(180, 156, 245, 0.3)',
                                  color: '#B49CF5',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.2)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.1)'
                                }}
                              >
                                <FiUpload size={14} className="inline mr-1" />
                                Upload
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  triggerCameraInput('front')
                                }}
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                                style={{
                                  background: 'rgba(180, 156, 245, 0.1)',
                                  border: '1px solid rgba(180, 156, 245, 0.3)',
                                  color: '#B49CF5',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.2)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.1)'
                                }}
                              >
                                <FiCamera size={14} className="inline mr-1" />
                                Camera
                              </button>
                            </div>
                            <input
                              ref={frontFileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageChange(e, 'front')}
                              className="hidden"
                            />
                            <input
                              ref={frontCameraInputRef}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => handleImageChange(e, 'front')}
                              className="hidden"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Back Image */}
                    <div>
                      <label className="label">Back</label>
                      <div
                        className={`
                        relative aspect-[3/4] border-2 border-dashed rounded-xl 
                        flex flex-col items-center justify-center cursor-pointer
                        transition-all duration-300
                        ${
                          backPreview
                            ? 'border-transparent'
                            : 'border-white/10 hover:border-cyan-500/50 hover:bg-white/5'
                        }
                      `}
                      >
                        {backPreview ? (
                          <>
                            <img
                              src={backPreview}
                              alt="Back preview"
                              className="w-full h-full object-cover rounded-xl"
                            />
                            <button
                              type="button"
                              onClick={() => removeImage('back')}
                              className="absolute top-2 right-2 bg-red-500 text-white p-1.5 rounded-full hover:bg-red-600 shadow-lg transition-colors"
                            >
                              <FiX size={16} />
                            </button>
                          </>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-3 sm:p-4 gap-2 sm:gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation()
                                triggerFileInput('back')
                              }}
                              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
                              style={{
                                background: 'rgba(180, 156, 245, 0.1)',
                                border: '1px solid rgba(180, 156, 245, 0.2)',
                              }}
                              aria-label="Upload back image"
                            >
                              <FiUpload className="text-purple-400" size={24} />
                            </button>
                            <p className="text-xs text-gray-500">PNG, JPG up to 10MB</p>
                            <div className="flex flex-col sm:flex-row gap-2 w-full max-w-[200px] sm:max-w-none">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  triggerFileInput('back')
                                }}
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                                style={{
                                  background: 'rgba(180, 156, 245, 0.1)',
                                  border: '1px solid rgba(180, 156, 245, 0.3)',
                                  color: '#B49CF5',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.2)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.1)'
                                }}
                              >
                                <FiUpload size={14} className="inline mr-1" />
                                Upload
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  triggerCameraInput('back')
                                }}
                                className="flex-1 min-w-0 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                                style={{
                                  background: 'rgba(180, 156, 245, 0.1)',
                                  border: '1px solid rgba(180, 156, 245, 0.3)',
                                  color: '#B49CF5',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.2)'
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = 'rgba(180, 156, 245, 0.1)'
                                }}
                              >
                                <FiCamera size={14} className="inline mr-1" />
                                Camera
                              </button>
                            </div>
                            <input
                              ref={backFileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleImageChange(e, 'back')}
                              className="hidden"
                            />
                            <input
                              ref={backCameraInputRef}
                              type="file"
                              accept="image/*"
                              capture="environment"
                              onChange={(e) => handleImageChange(e, 'back')}
                              className="hidden"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Info Box */}
                <div
                  className="rounded-xl p-4"
                  style={{
                    background: 'rgba(180, 156, 245, 0.1)',
                    border: '1px solid rgba(180, 156, 245, 0.2)',
                  }}
                >
                  <p className="text-sm text-cyan-300">
                    💡 Each submission consumes 1 M1NT credit. M1NT Lab can top up your balance when
                    needed.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-4 pt-6 border-t border-white/5">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="btn-secondary flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex-1"
                    disabled={loading || !ownerUserId || !connectedAddress}
                  >
                    {loading ? 'Submitting...' : 'Use 1 M1NT credit'}
                  </button>
                </div>

                {(!ownerUserId || !connectedAddress) && (
                  <div
                    className="text-center text-sm p-3 rounded-xl"
                    style={{
                      background: 'rgba(227, 184, 114, 0.1)',
                      border: '1px solid rgba(227, 184, 114, 0.3)',
                    }}
                  >
                    <span className="text-yellow-400">
                      {!ownerUserId
                        ? 'Account not ready. Please refresh.'
                        : 'Connect your wallet in the header to sign the ownership declaration and submit.'}
                    </span>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>

        {/* Camera Modal */}
        {cameraOpen && (
          <div className="fixed inset-0 flex items-center justify-center z-[60] p-2 sm:p-4 modal-overlay">
            <div
              className="modal-content max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center p-4 sm:p-6 border-b border-white/5 flex-shrink-0">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white">Capture Photo</h2>
                  <p className="text-xs sm:text-sm text-gray-400 mt-1">
                    {cameraSide === 'front' ? 'Card Front' : 'Card Back'}
                  </p>
                </div>
                <button
                  onClick={closeCamera}
                  className="p-2 hover:bg-white/5 rounded-xl transition-colors text-gray-400 hover:text-white"
                >
                  <FiX size={24} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto flex-1 min-h-0">
                {/* Camera Preview */}
                <div className="p-4 sm:p-6">
                  <div className="relative aspect-[3/4] bg-black rounded-xl overflow-visible">
                    <div className="relative w-full h-full rounded-xl overflow-hidden">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        className="w-full h-full object-cover"
                        style={{ transform: 'scaleX(-1)' }} // Mirror effect
                      />
                      {!stream && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                          <LoadingSpinner text="Opening camera..." />
                        </div>
                      )}
                    </div>

                    {/* Capture Button Overlay - Large circular button (hidden on mobile, visible on larger screens) */}
                    {stream && (
                      <div className="hidden sm:block absolute bottom-2 left-1/2 -translate-x-1/2 z-10">
                        <button
                          type="button"
                          onClick={capturePhoto}
                          className="w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center shadow-2xl transition-all hover:scale-110 active:scale-95 cursor-pointer"
                          style={{
                            background: 'linear-gradient(135deg, #00D9FF 0%, #B026FF 100%)',
                            border: '4px solid rgba(255, 255, 255, 0.5)',
                            boxShadow:
                              '0 0 40px rgba(180, 156, 245, 0.8), 0 4px 20px rgba(0, 0, 0, 0.5)',
                          }}
                        >
                          <FiCamera size={32} className="sm:w-9 sm:h-9 text-white" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Instructions */}
                  <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4 text-center">
                    {stream
                      ? 'Position the card within the area and click the capture button when ready'
                      : 'Waiting for camera access...'}
                  </p>

                  {/* Actions - Fixed at bottom for mobile */}
                  <div className="flex flex-col gap-3 sm:gap-4 mt-4 sm:mt-6 pb-2">
                    {/* Large Capture Button - Always visible when camera is open */}
                    <button
                      type="button"
                      onClick={capturePhoto}
                      disabled={!stream}
                      className={`w-full py-3 sm:py-4 rounded-xl flex items-center justify-center gap-2 sm:gap-3 font-bold text-base sm:text-lg transition-all ${
                        stream
                          ? 'hover:scale-[1.02] active:scale-[0.98] cursor-pointer'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      style={{
                        background: stream
                          ? 'linear-gradient(135deg, #00D9FF 0%, #B026FF 100%)'
                          : 'rgba(255, 255, 255, 0.1)',
                        boxShadow: stream ? '0 4px 20px rgba(180, 156, 245, 0.4)' : 'none',
                        color: 'white',
                        border: stream ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <FiCamera size={20} className="sm:w-6 sm:h-6" />
                      <span className="text-sm sm:text-base">
                        {stream ? 'Capture Image' : 'Waiting for camera...'}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={closeCamera}
                      className="btn-secondary w-full py-3 sm:py-4"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </>
  )
}
